/* eslint-disable jsx-a11y/anchor-is-valid */
/**
@format
@flow
*/

import React, {
  useEffect, useRef, useState,
} from 'react';
import styled from 'styled-components';
import { useParams, useLocation } from 'react-router-dom';
import { eventNames } from '@ziynx/analytics';
import { Helmet } from 'react-helmet-async';
import { InView } from 'react-intersection-observer';
import {
  withAppManager, getGraduationInfo, useNotification, LoadingSpinner,
} from '@ziynx/components/utilities';
import * as KrispyTokens from '@krispy-tokens/blue';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import ActionTypes from '@ziynx/action-types';
import Card from '@ziynx/components/cards';
import { ContentContainer, PageTemplate } from '@ziynx/components/layout';
import type { ListItem as ListItemType } from '@ziynx/flow-types/listItems';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import type { TalentProfileType } from '@ziynx/flow-types/talentProfile';
import TextEditorModal, { type Message } from '../Modals/TextEditorModal';
import { VIDEO_TRIGGER_TIME } from '../../constants';

type ActionDispatch = ({
  type: string,
  id?: string,
}) => void;

type Education = {
  degree: {
    _id: string,
    name: string,
    shorthand: string,
  },
  concentration: {
    _id: string,
    name: string,
  },
};

type Location = {
  _id: string,
  name: string,
};

type Interests = {
  _id: string,
  name: string,
};

type Props = $ReadOnly<{||}>;

function getButtonGroup(userInfo, emailEventLog: () => void, phoneEventLog: () => void) {
  const phone = userInfo?.phoneNumber || '555-555-5555';
  const email = userInfo?.email || 'help@ziynx.com';
  const onClick = userInfo?.onClick;
  const isWatched = userInfo?.isWatched;
  return [
    {
      variant: 'primary',
      title: isWatched ? 'Remove from list' : 'Add to watchlist',
      onClick,
    },
    {
      variant: 'icon',
      icon: 'email',
      href: `mailto:${email}`,
      onClick: () => emailEventLog(),
    },
    {
      variant: 'icon',
      icon: 'phone',
      onClick: async () => {
        await navigator?.clipboard?.writeText(phone);
        // eslint-disable-next-line no-alert
        alert('Phone number copied to clipboard.');
        phoneEventLog();
      },
    },
  ];
}

const ContentWrapper = styled.div`
  max-width: 688px;
  margin-bottom: 64px;
`;

const StatementWrapper = styled.div`
  margin-bottom: 64px;
`;

const NotesWrapper = styled.div`
  margin-top: 64px;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: ${KrispyTokens.KrispySpacingElementsLdXsmall}px;
`;

const BANNER_MESSAGE = {
  successAdd: 'has been added to your watchlist.',
  successRemove: 'has been removed from your watchlist.',
  error: 'An error occurred. Please try again later.',
  removing: 'Removing member.',
  adding: 'Adding member',
};

const ZIYNX_LOGO = 'https://ziynx-media-assets-dev.s3.amazonaws.com/ziynx_logo/ziynx-logo.png';

function getEducation(education: Array<Education>): Array<string> {
  if (!education?.length) return [];

  return education.map(({ degree, concentration }) => {
    if (!degree?.shorthand && !concentration?.name) return '';

    if (!concentration && !!degree) {
      return degree?.name;
    }

    return `${degree?.shorthand} in ${concentration?.name}`;
  });
}

function getUserInformation(
  education: Array<Education>,
  location: Array<Location>,
  interests: Array<Interests>,
): Array<ListItemType> {
  const transformedEducation: Array<string> = getEducation(education);

  return [
    {
      _id: '1',
      label: { text: 'Education' },
      text: transformedEducation,
      squircleText: '📚',
    },
    {
      _id: '2',
      label: { text: 'Location interests' },
      text: location?.length ? location.map((item): string => item.name) : [],
      squircleText: '📍',
    },
    {
      _id: '3',
      label: { text: 'Career interests' },
      text: interests?.length
        ? interests.map((item): string => item.name)
        : [],
      squircleText: '💼',
    },
  ];
}

const TalentProfileScreen = (props: ManagedProps<Props>): React$Node => {
  const { viewport, ...rest } = props;

  // const history = useHistory();
  const location = useLocation();
  // Grabs talent Id passed from Explore page
  const { talentId } = useParams();
  const dispatch: ActionDispatch = useDispatch();
  const [isPlaying, setPlaying] = useState(false);
  const [rightIsShown, setRightIsShown] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  // Start by showing overlay
  const [showOverlay, toggleOverlay] = useState(true);
  const [talentName, setTalentName] = useState('');
  const { notificationDispatch } = useNotification();
  const resetNotification = (isRemove = false) => {
    setTimeout(() => {
      if (isRemove) {
        dispatch({
          type: ActionTypes.watchlist.clearRemove,
        });
      } else {
        dispatch({
          type: ActionTypes.watchlist.clearAdd,
        });
      }
    }, 6000);
  };

  const videoRefLeft = useRef(null);
  const videoRefRight = useRef(null);

  const talentService: {
    ...TalentProfileType,
    fetching: boolean,
    error: any,
  } = useSelector(({ profile }) => (profile[talentId] || {}), shallowEqual);

  const [talentNotesHTMLString, setTalentNotesHTMLString] = useState(talentService?.ui?.talentNotes || '');

  const isPublic = useSelector(({ auth }) => {
    const { isLoggedIn } = auth[ActionTypes.auth.checkIfLoggedIn.GROUP] || {};
    return !isLoggedIn;
  }, shallowEqual);

  const watchlistService = useSelector(({ watchlist }) => ({
    addUser: watchlist[ActionTypes.watchlist.add.GROUP] || {},
    removeUser: watchlist[ActionTypes.watchlist.remove.GROUP] || {},
  }), shallowEqual);

  const responseId = talentService?.myShot?.responseId;
  const amIWatched = talentService?.ui?.watched;

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch({
      type: ActionTypes.profile.get.START,
      id: talentId,
    });
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.navigated_talent_profile,
      ...(location?.state?.from && {
        properties: {
          referrer: location?.state?.from,
        },
      }),
    });
  }, []);

  // Sending out notification that profile was viewed
  useEffect(() => {
    if (!talentService?.viewership?.[talentId]) {
      dispatch({
        type: ActionTypes.trackingAction.viewedProfile.START,
        profileViewed: talentId,
      });
    }
  }, [talentService?.viewership]);

  const onPaginate = (inView): void => {
    if (talentService?.statementResponsePagination?.isLastPage
      || talentService?.fetching || !inView) return;
    dispatch({
      type: ActionTypes.profile.getResponses.START,
      id: talentId,
      page: (talentService?.statementResponsePagination?.page ?? 0) + 1,
      pageSize: 5,
    });
  };

  /** REMOVING FROM WATCHLIST */
  useEffect(() => {
    if (watchlistService?.removeUser?.success
      && !watchlistService?.removeUser?.fetching
      && talentName) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'success',
          content: `${talentName} ${BANNER_MESSAGE?.successRemove}`,
        },
      });
      resetNotification(true);
    }
    if (watchlistService?.removeUser?.error && !watchlistService?.removeUser?.fetching) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          content: BANNER_MESSAGE.error,
          variant: 'error',
        },
      });
      resetNotification(true);
    }
  }, [
    watchlistService?.removeUser?.success,
    watchlistService?.removeUser?.fetching,
    watchlistService?.removeUser?.error,
    talentName,
  ]);

  /** ADDING TO WATCHLIST */
  useEffect(() => {
    if (watchlistService?.addUser?.success && !watchlistService?.addUser?.fetching && talentName) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          content: `${talentName} ${BANNER_MESSAGE?.successAdd}`,
          variant: 'success',
        },
      });
      resetNotification();
    }
    if (watchlistService?.addUser?.error && !watchlistService?.addUser?.fetching) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          content: BANNER_MESSAGE.error,
          variant: 'error',
        },
      });
      resetNotification();
    }
  }, [watchlistService?.addUser?.success,
    watchlistService?.addUser?.fetching,
    watchlistService?.addUser?.error,
    talentName]);

  const fullName = `${talentService?.firstName ?? ''} ${talentService?.lastName ?? ''}`;

  const handleWatchlistAction = (firstName, lastName, graduation, myShot) => {
    setTalentName(fullName);
    if (amIWatched) {
      dispatch({
        type: ActionTypes.watchlist.remove.START,
        body: {
          userId: talentId,
        },
        userInfo: { _id: talentId },
      });
      dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames.talent_profile_remove_from_watchlist,
        properties: {
          talentId,
        },
      });
    }
    if (!amIWatched) {
      setTalentName(fullName);
      dispatch({
        type: ActionTypes.watchlist.add.START,
        body: {
          userId: talentId,
        },
        userInfo: {
          _id: talentId,
          firstName,
          lastName,
          graduation,
          myShot,
        },
      });
      dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames.talent_profile_add_to_watchlist,
        properties: {
          talentId,
        },
      });
    }
  };

  const userContact = {
    phoneNumber: talentService?.phoneNumber,
    email: talentService?.email,
    isWatched: amIWatched,
    onClick: () => handleWatchlistAction(
      talentService?.firstName,
      talentService?.lastName,
      talentService?.graduation,
      talentService?.myShot,
    ),
  };
  const screenTitle = `${fullName} | ZIYNX`;

  const userDetails: Array<ListItemType> = getUserInformation(
    talentService?.education,
    talentService?.location,
    talentService?.interests,
  );

  const _onClick = () => {
    toggleOverlay(false);
    setPlaying(true);
  };

  // ==== VIDEO HANDLING ==== //

  const _pauseRight = () => {
    if (rightIsShown) {
      setPlaying(false);
    }
  };

  const _pauseLeft = () => {
    if (!rightIsShown) {
      setPlaying(false);
    }
  };

  const syncLeftToRight = () => {
    const videoLeft: ?HTMLVideoElement = videoRefLeft?.current;
    const videoRight: ?HTMLVideoElement = videoRefRight?.current;
    if (videoLeft && videoRight?.currentTime) {
      videoLeft.currentTime = videoRight.currentTime;
    }
    if ((videoRight && videoRight?.currentTime >= VIDEO_TRIGGER_TIME)
    && !talentService?.viewership?.[responseId]) {
      // Sending out notification that MyShot was viewed
      dispatch({
        type: ActionTypes.trackingAction.viewedMyShot.START,
        videoViewed: responseId,
      });
    }
  };

  const syncRightToLeft = () => {
    const videoLeft: ?HTMLVideoElement = videoRefLeft?.current;
    const videoRight: ?HTMLVideoElement = videoRefRight?.current;
    if (videoRight && videoLeft?.currentTime) {
      videoRight.currentTime = videoLeft.currentTime;
    }
    if ((videoLeft && videoLeft?.currentTime >= VIDEO_TRIGGER_TIME)
    && !talentService?.viewership?.[responseId]) {
      dispatch({
        type: ActionTypes.trackingAction.viewedMyShot.START,
        videoViewed: responseId,
      });
    }
  };

  const _onEnd = () => {
    const rightVideo: ?HTMLVideoElement = videoRefRight?.current;
    const leftVideo: ?HTMLVideoElement = videoRefLeft?.current;
    toggleOverlay(true);
    /* eslint-disable no-param-reassign */
    // Make sure we can restart at beginning when finished
    // We need to ensure this is the case here because otherwise one may be out of sync
    [leftVideo, rightVideo].forEach((video) => {
      if (video && video?.currentTime !== 0) {
        video.pause();
        video.currentTime = 0;
      }
    });
    /* eslint-enable no-param-reassign */
  };

  useEffect(() => {
    const videoLeft: ?HTMLVideoElement = videoRefLeft?.current;
    const videoRight: ?HTMLVideoElement = videoRefRight?.current;
    // Always sync
    (rightIsShown ? syncRightToLeft : syncLeftToRight)();
    if (isPlaying) {
      if (rightIsShown && videoRight) {
        if (videoLeft && !videoLeft.paused) videoLeft.pause();
        videoRight.play();
      } else if (!rightIsShown && videoLeft) {
        if (videoRight && !videoRight.paused) videoRight.pause();
        videoLeft.play();
      }
    }
  }, [rightIsShown, isPlaying]);

  // ==== END VIDEO HANDLING ==== //

  const thumbnail = talentService?.myShot?.sources?.thumbnail_optimized?.url
  || talentService?.myShot?.sources?.thumbnail?.url;

  const videoSource = talentService?.myShot?.sources?.mp4_square?.url
  || talentService?.myShot?.sources?.mp4_square?.url;

  const logEmailEvent = () => {
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.talent_profile_email_talent,
      properties: {
        talentId,
      },
    });
  };

  const logPhoneEvent = () => {
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.talent_profile_copy_talent_phone_number,
      properties: {
        talentId,
      },
    });
  };

  const buttonGroup = isPublic
    ? undefined
    : { data: getButtonGroup(userContact, logEmailEvent, logPhoneEvent) };

  const profileUrl = `${window?.location?.hostname}${window?.location?.pathname}`;

  const linkInfo = {
    children: 'Copy public profile URL',
    onClick: () => {
      navigator?.clipboard?.writeText(profileUrl);
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'success',
          content: 'Public profile URL has been copied to clipboard.',
        },
      });
      resetNotification(true);
    },
  };

  const patchTalentNotes = (htmlString?: string) => {
    // Patch the talent notes and close the modal
    dispatch({
      type: ActionTypes.profile.putTalentNotes.START,
      id: talentId,
      body: {
        talentNotes: htmlString || '',
      },
    });
    setTalentNotesHTMLString(htmlString);
    setShowNotesModal(false);
  };

  const _renderTalentNotes = (talentNotes) => {
    const noteLinkInfo = {
      children: 'Edit',
      onClick: () => {
        setShowNotesModal(true);
        dispatch({
          type: ActionTypes.analytics.logEvent.START,
          name: eventNames.editing_talent_notes,
          properties: {
            talent: talentId,
          },
        });
      },
    };
    return (
      <NotesWrapper>
        <Card
          variant="note"
          loading={!!talentService.fetching}
          label="Note"
          link={noteLinkInfo}
          content={talentNotes}
          squircleText="📌"
        />
      </NotesWrapper>
    );
  };

  const tagInfo = {
    label: talentService?.ui?.jobStatus,
    variant: 'green',
  };

  const leftSlot = (): React$Node => (
    <ContentWrapper>
      {/* <LinkWrapper>
        <Link
          variant="caret"
          onClick={() => history.push(history.location.state?.backLink || '/discover',
            { title: history.location.state?.name })}
          color={Colors.GrayDark}
        >
          Back to&nbsp;
          {history.location.state?.name || 'Discover'}
        </Link>
      </LinkWrapper> */}
      <Card
        variant="profile"
        hideOverlay={!showOverlay}
        eyebrow={getGraduationInfo(talentService?.graduation)}
        title={fullName}
        link={linkInfo}
        {...(talentService?.ui?.jobStatus === 'Actively looking' && { tag: tagInfo })}
        subtitle={talentService?.headline}
        src={videoSource || ' '}
        imageSource={thumbnail || ZIYNX_LOGO}
        listItems={userDetails}
        buttonGroup={buttonGroup}
        onPlay={() => {
          dispatch({
            type: ActionTypes.analytics.logEvent.START,
            name: eventNames.viewed_myshot,
            properties: {
              videoType: 'myShot',
              talentId,
              videoId: talentService?.myShot?._id,
            },
          });
        }}
        getRef={videoRefLeft}
        onClick={_onClick}
        onPause={_pauseLeft}
        onSeekEnd={syncRightToLeft}
        onSeek={syncRightToLeft}
        onTimeUpdate={syncRightToLeft}
        onEnd={_onEnd}
        viewport={viewport}
      />
      {_renderTalentNotes(talentNotesHTMLString)}
    </ContentWrapper>
  );

  const leftContent = ({ responses }: {
    responses: Array<Object>,
  }): React$Node => {
    if (!responses?.length) return null;
    return (
      <>
        {responses?.map((response, index) => {
          if (!response || !response?.questionType) return null;
          switch (response?.questionType) {
          case 'text':
            if (!response?.data?.length) return null;
            return (
              <StatementWrapper key={response?._id + index}>
                <Card
                  variant="textStatement"
                  headline={{
                    title: {
                      text: response?.question?.question,
                    },
                  }}
                  data={response?.data}
                />
              </StatementWrapper>
            );
          case 'video':
            return (
              <StatementWrapper key={response?._id + index}>
                <Card
                  variant="videoSquare"
                  onPlay={() => {
                    dispatch({
                      type: ActionTypes.analytics.logEvent.START,
                      name: eventNames.viewed_video_response,
                      properties: {
                        videoType: 'statement',
                        question: response?.question?.question,
                        talentId,
                        videoId: talentService?.myShot?._id,
                      },
                    });
                  }}
                  src={response?.video?.sources?.mp4_square?.url}
                  imageSource={response?.video?.sources?.thumbnail_optimized_png?.url
                  || response?.video?.sources?.thumbnail_png?.url
                  || response?.video?.sources?.thumbnail_square?.url
                  || response?.video?.sources?.thumbnail.url}
                  title={response?.question?.question}
                />
              </StatementWrapper>
            );
          default:
            return null;
          }
        })}
        <InView as="div" onChange={onPaginate} rootMargin="0px 0px 100px 0px">
          <SpinnerWrapper>
            {
              // eslint-disable-next-line max-len
              (!talentService?.statementResponsePagination?.isLastPage && talentService?.fetching) && <LoadingSpinner />
            }
          </SpinnerWrapper>
        </InView>
      </>
    );
  };

  const rightSlot = (): React$Node => (
    <Card
      variant="profile"
      isScrolled
      hideOverlay={!showOverlay}
      eyebrow={getGraduationInfo(talentService?.graduation)}
      title={fullName}
      link={linkInfo}
      {...(talentService?.ui?.jobStatus === 'Actively looking' && { tag: tagInfo })}
      subtitle={talentService?.headline}
      src={videoSource || ' '}
      imageSource={thumbnail || ZIYNX_LOGO}
      listItems={userDetails}
      buttonGroup={buttonGroup}
      getRef={videoRefRight}
      onClick={_onClick}
      onPause={_pauseRight}
      onSeekEnd={syncLeftToRight}
      onSeek={syncLeftToRight}
      onEnd={_onEnd}
    />
  );

  const getOffset = () => {
    if (viewport === 'mobileSmall' || viewport === 'mobile') {
      return '84px';
    }
    return '94px';
  };

  const stickyHeaderData = {
    textLinks: [
      {
        children: amIWatched ? 'Remove from list' : 'Add',
        onClick: handleWatchlistAction,
      },
    ],
    title: { text: fullName },
    topOffset: getOffset(),
    squircleButton: {
      icon: {
        name: amIWatched ? 'checkmark-fill' : 'plus',
      },
      inverted: amIWatched,

    },
    onClick: handleWatchlistAction, // For SquircleButton functionality
  };

  const _topOffset = () => {
    if (viewport === 'mobile' || viewport === 'mobileSmall') {
      return 86;
    }
    return 96;
  };
  return (
    <>
      <Helmet>
        <title>{screenTitle}</title>
      </Helmet>
      <ContentContainer offsetTop={isPublic ? 10 : _topOffset()} {...rest}>
        <TextEditorModal
          open={showNotesModal}
          content={talentNotesHTMLString}
          buttonGroup={{
            primary: {
              children: 'Save',
              onClick: (message?: Message) => patchTalentNotes(message?.htmlString),
            },
            secondary: {
              children: 'Cancel',
              onClick: () => setShowNotesModal(false),
            },
          }}
        />
        <PageTemplate
          leftSlot={leftSlot()}
          leftContent={leftContent({ responses: talentService?.statementResponses })}
          rightSlot={rightSlot()}
          onSlotShown={setRightIsShown}
          stickyHeader={isPublic ? undefined : stickyHeaderData}
          rightSlotMarginLeft="40px"
        />
      </ContentContainer>
    </>
  );
};

export default (withAppManager(TalentProfileScreen): React$ComponentType<ManagedProps<Props>>);
